import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import { NoteContextType } from '@dataTypes/note.type'
import {
  NotificationPriority,
  NotificationTriggerType,
} from '@dataTypes/notification.types'
import moment from 'moment'

/** Chart / perio / treatment-plan notes — used for Chart entry updated toasts. */
export const isChartingNoteContext = (contextType?: string | null): boolean => {
  return (
    contextType === NoteContextType.CHARTING ||
    contextType === NoteContextType.PERIO ||
    contextType === NoteContextType.TREATMENT_PLAN
  )
}

/**
 * Utility function to create a notification toast for Allergy Alert
 */
export const createAllergyAlertToast = (
  patientName: string,
  allergen: string,
): NotificationToastData => {
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.CRITICAL,
    title: 'Allergy Alert',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: `${patientName} is allergic to ${allergen}.`,
    actionButtonText: 'Update treatment plan',
  }
}

/**
 * Utility function to create a notification toast for Pending Forms
 */
export const createPendingFormsToast = (): NotificationToastData => {
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.URGENT,
    title: 'Pending Forms',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: 'There are pending forms that require your attention.',
    actionButtonText: 'View Forms',
  }
}

/**
 * Utility function to create a notification toast for New Appointment
 */
export const createNewAppointmentToast = (
  patientName?: string,
): NotificationToastData => {
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.LOW,
    title: 'New appointment has been booked',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: patientName
      ? `A new appointment has been booked for ${patientName}.`
      : 'A new appointment has been booked.',
    actionButtonText: 'View Appointment',
  }
}

/** e.g. "July 20, Monday, 2:00pm" */
export const formatScheduledAppointmentWhen = (
  apptDate: string | Date,
  apptStartTime: string,
): string => {
  const datePart = moment(apptDate)
  const timePart = moment(apptStartTime, ['HH:mm:ss', 'HH:mm', 'h:mm A'], true)
  if (!datePart.isValid() || !timePart.isValid()) {
    return ''
  }

  return datePart
    .clone()
    .hour(timePart.hour())
    .minute(timePart.minute())
    .second(0)
    .format('MMMM D, dddd, h:mma')
}

/**
 * Utility function to create a success notification toast
 */
export const createSuccessToast = (
  title: string,
  message: string,
  actionButtonText?: string,
  onAction?: () => void,
): NotificationToastData => {
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.SUCCESS,
    title,
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message,
    actionButtonText: actionButtonText || '',
    onAction,
  }
}

/**
 * Toast after setting a new appointment.
 * Sample: "An appointment on July 20, Monday, 2:00pm has been scheduled."
 */
export const createScheduledAppointmentToast = (
  apptDate: string | Date,
  apptStartTime: string,
): NotificationToastData => {
  const when = formatScheduledAppointmentWhen(apptDate, apptStartTime)
  return createSuccessToast(
    'Appointment scheduled successfully',
    when
      ? `An appointment on ${when} has been scheduled.`
      : 'An appointment has been scheduled.',
  )
}

/** e.g. "Room no. 2" */
export const formatMovedAppointmentRoom = (roomName: string): string => {
  const trimmed = roomName.trim()
  if (!trimmed) return 'Room no. unknown'
  if (/^room no\./i.test(trimmed)) return trimmed
  const roomNumber = trimmed.match(/\d+/)
  if (roomNumber) return `Room no. ${roomNumber[0]}`
  return `Room no. ${trimmed}`
}

/**
 * Toast after rescheduling an appointment (drag or click).
 * Sample: "An appointment from Room no. 2 has been moved to Room no. 1"
 */
export const createMovedAppointmentToast = (
  fromRoom: string,
  toRoom: string,
  onViewDetails?: () => void,
): NotificationToastData => {
  return {
    ...createSuccessToast(
      'Move appointment',
      `An appointment from ${formatMovedAppointmentRoom(fromRoom)} has been moved to ${formatMovedAppointmentRoom(toRoom)}`,
      'View details',
      onViewDetails,
    ),
    secondaryButtonText: 'Dismiss',
  }
}

/**
 * Toast after cancelling an appointment.
 * Sample: "July 20, Monday, 2:00pm appointment has been cancelled."
 */
export const createCanceledAppointmentToast = (
  apptDate: string | Date,
  apptStartTime: string,
): NotificationToastData => {
  const when = formatScheduledAppointmentWhen(apptDate, apptStartTime)
  return createSuccessToast(
    'Appointment canceled',
    when
      ? `${when} appointment has been cancelled.`
      : 'The appointment has been cancelled.',
  )
}

/**
 * Toast after creating a form template.
 */
export const createFormTemplateCreatedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Created successfully',
    'Your form template has been saved.',
  )
}

/**
 * Toast after updating a form template.
 */
export const createFormTemplateUpdatedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Updated successfully',
    'The template has been updated.',
  )
}

/**
 * Toast after approving a form template.
 */
export const createFormTemplateApprovedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Approved successfully',
    'The form template has been approved.',
  )
}

/**
 * Toast after deleting a form template.
 */
export const createFormTemplateDeletedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Deleted successfully',
    'The form template has been deleted.',
  )
}

/**
 * Toast after generating a kiosk OTP for forms.
 */
export const createKioskOtpGeneratedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Generated successfully',
    'Kiosk OTP generated successfully.',
  )
}

/**
 * Toast after sending forms to a patient.
 */
export const createFormsSentToast = (): NotificationToastData => {
  return createSuccessToast(
    'Forms sent successfully',
    'Forms have been sent to the patient.',
  )
}

/**
 * Toast after a new diagnostic code is added to the patient's chart.
 */
export const createNewConditionChartedToast = (
  onViewChart?: () => void,
): NotificationToastData => {
  return createSuccessToast(
    'New condition charted',
    "A new diagnostic code has been added to the patient's chart.",
    'View chart',
    onViewChart,
  )
}

/**
 * Toast after a provider edits a finalized chart note (queued for admin review).
 */
export const createChartEntryUpdatedToast = (
  onViewAuditLog?: () => void,
): NotificationToastData => {
  return createSuccessToast(
    'Chart entry updated',
    'A provider edit has been logged and queued for administrative review.',
    'View chart audit log',
    onViewAuditLog,
  )
}

/**
 * Toast after creating a dental/perio chart.
 */
export const createDentalChartAddedToast = (
  chartName: string,
  patientName: string,
  onViewChart?: () => void,
): NotificationToastData => {
  const name = chartName.trim() || 'chart'
  const patient = patientName.trim() || 'patient'
  return createSuccessToast(
    'New dental chart entry added',
    `New dental chart "${name}" added for ${patient}.`,
    'View chart',
    onViewChart,
  )
}

/**
 * Toast after updating a dental/perio chart.
 */
export const createDentalChartUpdatedToast = (
  chartName: string,
  patientName: string,
  onViewChart?: () => void,
): NotificationToastData => {
  const name = chartName.trim() || 'chart'
  const patient = patientName.trim() || 'patient'
  return createSuccessToast(
    'Updated successfully',
    `New dental chart "${name}" updated for ${patient}.`,
    'View chart',
    onViewChart,
  )
}

/**
 * Toast after deleting a chart.
 */
export const createDentalChartDeletedToast = (
  chartName: string,
): NotificationToastData => {
  const name = chartName.trim() || 'chart'
  return createSuccessToast(
    'Deleted successfully',
    `${name} has been deleted successfully.`,
  )
}

/**
 * Toast after creating a treatment plan.
 */
export const createTreatmentPlanCreatedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Created successfully',
    'Treatment plan has been created successfully.',
  )
}

/**
 * Toast after updating a treatment plan.
 */
export const createTreatmentPlanUpdatedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Treatment plan updated successfully',
    'Treatment plan has been updated successfully.',
  )
}

/**
 * Toast after deleting a treatment plan.
 */
export const createTreatmentPlanDeletedToast = (
  planName: string,
): NotificationToastData => {
  const name = planName.trim() || 'Treatment plan'
  return createSuccessToast(
    'Treatment plan deleted successfully',
    `${name} has been deleted successfully.`,
  )
}

/** Spec copy for Charting/Treatment realtime notifications. */
export const getChartingNotificationToastCopy = (
  type?: string,
): { title: string; message: string; actionButtonText: string } | null => {
  if (type === NotificationTriggerType.CHART_NEW_CONDITION_ADDED) {
    return {
      title: 'New condition charted',
      message: "A new diagnostic code has been added to the patient's chart.",
      actionButtonText: 'View chart',
    }
  }
  if (type === NotificationTriggerType.CHART_ENTRY_EDITED) {
    return {
      title: 'Chart entry updated',
      message:
        'A provider edit has been logged and queued for administrative review.',
      actionButtonText: 'View chart audit log',
    }
  }
  return null
}

export const CLAIMS_FEE_SCHEDULE_SETTINGS_PATH =
  '/settings/insurance-and-payments/fee-schedule'

export type ClaimsNotificationToastCopy = {
  title: string
  message: string
  actionButtonText: string
  secondaryButtonText?: string
  fallbackPath: string | null
  openClaim?: boolean
}

/** Spec copy for Claims realtime notifications. */
export const getClaimsNotificationToastCopy = (
  type?: string,
): ClaimsNotificationToastCopy | null => {
  switch (type) {
    case NotificationTriggerType.CLAIM_REJECTED:
      return {
        title: 'Claim submission rejected',
        message:
          'The electronic claim failed clearinghouse validation and requires immediate correction.',
        actionButtonText: 'View claim',
        secondaryButtonText: 'Dismiss',
        fallbackPath: '/claims',
        openClaim: true,
      }
    case NotificationTriggerType.CLAIM_DENIED:
      return {
        title: 'Insurance claim denied',
        message: 'The insurance payer denied payment for this processed claim.',
        actionButtonText: 'View claim',
        secondaryButtonText: 'Dismiss',
        fallbackPath: '/claims',
        openClaim: true,
      }
    case NotificationTriggerType.CLAIM_PENDING_OVER_30_DAYS:
      return {
        title: 'Claim aging past 30 days',
        message:
          'An outstanding claim has been pending payment for over 30 days.',
        actionButtonText: 'Check status',
        secondaryButtonText: 'Dismiss',
        fallbackPath: '/claims',
        openClaim: true,
      }
    case NotificationTriggerType.CLAIM_PAYMENT_DISCREPANCY:
      return {
        title: 'Payment discrepancy detected',
        message:
          'A significant variance was detected between expected and posted payment amounts.',
        actionButtonText: 'Review payment',
        secondaryButtonText: 'Dismiss',
        fallbackPath: '/claims',
        openClaim: true,
      }
    case NotificationTriggerType.CLAIM_ATTACHMENTS_REQUIRED:
      return {
        title: 'Attachments required',
        message:
          'Additional documentation is required to submit this claim based on the procedure code.',
        actionButtonText: 'View claim',
        secondaryButtonText: 'Dismiss',
        fallbackPath: '/claims',
        openClaim: true,
      }
    case NotificationTriggerType.CLAIM_FINALIZED:
      return {
        title: 'Claim closed',
        message:
          'The claim status has been updated to finalized with payment posted.',
        actionButtonText: 'Clear notification',
        fallbackPath: null,
      }
    case NotificationTriggerType.CLAIM_NEW_PAYER_FEE_SCHEDULE:
      return {
        title: 'Fee schedule updated',
        message:
          'A new payer fee schedule has been imported and updated in the system.',
        actionButtonText: 'Review new rates',
        fallbackPath: CLAIMS_FEE_SCHEDULE_SETTINGS_PATH,
      }
    default:
      return null
  }
}

const createClaimsSpecToast = (
  type: NotificationTriggerType,
  priority: NotificationPriority,
  onAction?: () => void,
): NotificationToastData => {
  const copy = getClaimsNotificationToastCopy(type)
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority,
    title: copy?.title ?? '',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: copy?.message ?? '',
    actionButtonText: copy?.actionButtonText ?? '',
    onAction,
    ...(copy?.secondaryButtonText
      ? { secondaryButtonText: copy.secondaryButtonText }
      : {}),
  }
}

export const createClaimSubmissionRejectedToast = (
  onViewClaim?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_REJECTED,
    NotificationPriority.CRITICAL,
    onViewClaim,
  )

export const createInsuranceClaimDeniedToast = (
  onViewClaim?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_DENIED,
    NotificationPriority.URGENT,
    onViewClaim,
  )

export const createClaimAgingPast30DaysToast = (
  onCheckStatus?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_PENDING_OVER_30_DAYS,
    NotificationPriority.URGENT,
    onCheckStatus,
  )

export const createPaymentDiscrepancyDetectedToast = (
  onReviewPayment?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_PAYMENT_DISCREPANCY,
    NotificationPriority.URGENT,
    onReviewPayment,
  )

export const createClaimAttachmentsRequiredToast = (
  onViewClaim?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_ATTACHMENTS_REQUIRED,
    NotificationPriority.URGENT,
    onViewClaim,
  )

export const createClaimClosedToast = (): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_FINALIZED,
    NotificationPriority.SUCCESS,
  )

export const createFeeScheduleUpdatedToast = (
  onReviewRates?: () => void,
): NotificationToastData =>
  createClaimsSpecToast(
    NotificationTriggerType.CLAIM_NEW_PAYER_FEE_SCHEDULE,
    NotificationPriority.SUCCESS,
    onReviewRates,
  )

export const BILLING_STRIPE_SETTINGS_PATH =
  '/settings/insurance-and-payments/accounting/stripe'
export const BILLING_PAYMENTS_PATH = '/billing/payments'

export type BillingNotificationToastCopy = {
  title: string
  message: string
  actionButtonText: string
  secondaryButtonText?: string
  fallbackPath: string
}

/** Spec copy for Billing realtime notifications. */
export const getBillingNotificationToastCopy = (
  type?: string,
): BillingNotificationToastCopy | null => {
  switch (type) {
    case NotificationTriggerType.BILLING_PAYMENT_PROCESSOR_FAILURE:
      return {
        title: 'Payment gateway disconnected',
        message:
          'The system lost connection to the integrated payment processor. Card processing is unavailable.',
        actionButtonText: 'Reconnect gateway',
        secondaryButtonText: 'Dismiss',
        fallbackPath: BILLING_STRIPE_SETTINGS_PATH,
      }
    case NotificationTriggerType.BILLING_CARD_DECLINED:
      return {
        title: 'Payment card declined',
        message: 'An automated charge attempt failed for the card on file.',
        actionButtonText: 'View details',
        secondaryButtonText: 'Dismiss',
        fallbackPath: BILLING_PAYMENTS_PATH,
      }
    case NotificationTriggerType.BILLING_PAYMENT_PLAN_DUE_TODAY:
      return {
        title: 'Payment plan due today',
        message:
          'A scheduled payment plan installment is due today for processing.',
        actionButtonText: 'View payment plan',
        secondaryButtonText: 'Dismiss',
        fallbackPath: BILLING_PAYMENTS_PATH,
      }
    default:
      return null
  }
}

const createBillingSpecToast = (
  type: NotificationTriggerType,
  priority: NotificationPriority,
  onAction?: () => void,
): NotificationToastData => {
  const copy = getBillingNotificationToastCopy(type)
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority,
    title: copy?.title ?? '',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: copy?.message ?? '',
    actionButtonText: copy?.actionButtonText ?? '',
    onAction,
    ...(copy?.secondaryButtonText
      ? { secondaryButtonText: copy.secondaryButtonText }
      : {}),
  }
}

export const createPaymentGatewayDisconnectedToast = (
  onReconnect?: () => void,
): NotificationToastData =>
  createBillingSpecToast(
    NotificationTriggerType.BILLING_PAYMENT_PROCESSOR_FAILURE,
    NotificationPriority.CRITICAL,
    onReconnect,
  )

export const createPaymentCardDeclinedToast = (
  onViewDetails?: () => void,
): NotificationToastData =>
  createBillingSpecToast(
    NotificationTriggerType.BILLING_CARD_DECLINED,
    NotificationPriority.URGENT,
    onViewDetails,
  )

export const createPaymentPlanDueTodayToast = (
  onViewPaymentPlan?: () => void,
): NotificationToastData =>
  createBillingSpecToast(
    NotificationTriggerType.BILLING_PAYMENT_PLAN_DUE_TODAY,
    NotificationPriority.URGENT,
    onViewPaymentPlan,
  )

export const MESSAGE_CENTER_CHAT_PATH = '/message'
export const MESSAGE_CENTER_CALLS_PATH = '/message'

export type MessageCenterNotificationToastCopy = {
  title: string
  message: string
  actionButtonText: string
  secondaryButtonText?: string
  fallbackPath: string | null
  navState?: Record<string, unknown>
  openInternalChat?: boolean
}

/** Spec copy for Message Center realtime notifications. */
export const getMessageCenterNotificationToastCopy = (
  type?: string,
): MessageCenterNotificationToastCopy | null => {
  switch (type) {
    case NotificationTriggerType.MESSAGE_EMERGENCY_RECEIVED:
      return {
        title: 'Emergency message flagged',
        message:
          'A high-priority patient message containing emergency keywords requires immediate triage.',
        actionButtonText: 'View message',
        secondaryButtonText: 'Dismiss',
        fallbackPath: MESSAGE_CENTER_CHAT_PATH,
        navState: { tab: 'chat' },
      }
    case NotificationTriggerType.MESSAGE_NEW_UNREAD:
      return {
        title: 'Unanswered message alert',
        message:
          'A patient message has exceeded the response threshold and remains unanswered.',
        actionButtonText: 'View message',
        secondaryButtonText: 'Dismiss',
        fallbackPath: MESSAGE_CENTER_CHAT_PATH,
        navState: { tab: 'chat' },
      }
    case NotificationTriggerType.MESSAGE_MISSED_CALL_NO_MATCH:
      return {
        title: 'Missed call logged',
        message:
          'An inbound phone call was missed and could not be matched to an existing patient record.',
        actionButtonText: 'View call log',
        secondaryButtonText: 'Dismiss',
        fallbackPath: MESSAGE_CENTER_CALLS_PATH,
        navState: { tab: 'calls' },
      }
    case NotificationTriggerType.MESSAGE_INTERNAL_TEAM:
      return {
        title: 'New team message',
        message:
          'You have a new direct message or mention in the internal chat.',
        actionButtonText: 'Open chat',
        secondaryButtonText: 'Dismiss',
        fallbackPath: null,
        openInternalChat: true,
      }
    default:
      return null
  }
}

const createMessageCenterSpecToast = (
  type: NotificationTriggerType,
  priority: NotificationPriority,
  onAction?: () => void,
): NotificationToastData => {
  const copy = getMessageCenterNotificationToastCopy(type)
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority,
    title: copy?.title ?? '',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: copy?.message ?? '',
    actionButtonText: copy?.actionButtonText ?? '',
    onAction,
    ...(copy?.secondaryButtonText
      ? { secondaryButtonText: copy.secondaryButtonText }
      : {}),
  }
}

export const createEmergencyMessageFlaggedToast = (
  onViewMessage?: () => void,
): NotificationToastData =>
  createMessageCenterSpecToast(
    NotificationTriggerType.MESSAGE_EMERGENCY_RECEIVED,
    NotificationPriority.CRITICAL,
    onViewMessage,
  )

export const createUnansweredMessageAlertToast = (
  onViewMessage?: () => void,
): NotificationToastData =>
  createMessageCenterSpecToast(
    NotificationTriggerType.MESSAGE_NEW_UNREAD,
    NotificationPriority.URGENT,
    onViewMessage,
  )

export const createMissedCallLoggedToast = (
  onViewCallLog?: () => void,
): NotificationToastData =>
  createMessageCenterSpecToast(
    NotificationTriggerType.MESSAGE_MISSED_CALL_NO_MATCH,
    NotificationPriority.URGENT,
    onViewCallLog,
  )

export const createNewTeamMessageToast = (
  onOpenChat?: () => void,
): NotificationToastData =>
  createMessageCenterSpecToast(
    NotificationTriggerType.MESSAGE_INTERNAL_TEAM,
    NotificationPriority.SUCCESS,
    onOpenChat,
  )

/** When the user turns on the Enamelai switch in Report Preview (or related report screens). */
export const createEnamelAiReportsEnabledToast = (): NotificationToastData => {
  return {
    id: `toast-enamel-ai-reports-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.SUCCESS,
    title: 'Enamelai AI has been enabled',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message:
      'AI has been enabled in Reports. You may now use it to enhance your report documents.',
    actionButtonText: '',
  }
}

/**
 * Toast after moving a single file.
 * Sample: `"PDF" has been moved to "X-rays" folder.`
 */
export const createFileMovedToast = (
  fileType: string,
  folderName: string,
  onViewFolder?: () => void,
): NotificationToastData => {
  return createSuccessToast(
    'A file has been moved',
    `"${fileType}" has been moved to "${folderName}" folder.`,
    'View folder',
    onViewFolder,
  )
}

/**
 * Toast after moving multiple files.
 * Sample: `All selected files have been moved to "X-rays" folder.`
 */
export const createMultipleFilesMovedToast = (
  folderName: string,
  onViewFolder?: () => void,
): NotificationToastData => {
  return createSuccessToast(
    'Multiple files have been moved',
    `All selected files have been moved to "${folderName}" folder.`,
    'View folder',
    onViewFolder,
  )
}

/** Toast after successfully copying folder(s). */
export const createFoldersCopiedToast = (): NotificationToastData => {
  return createSuccessToast(
    'Copied successfully',
    'Successfully created copies of selected folders.',
  )
}

/** Toast after successfully renaming a file or folder. */
export const createRenamedSuccessfullyToast = (
  oldName: string,
  newName: string,
): NotificationToastData => {
  return createSuccessToast(
    'Renamed successfully',
    `"${oldName}" has been renamed to "${newName}".`,
  )
}

/**
 * Toast after attaching a file to a claim or claim procedure.
 * Sample: `"X-ray.pdf" has been attached to Jane Doe's claim.`
 */
export const createFileAttachedToClaimToast = (
  fileName: string,
  patientName: string,
  onViewClaim?: () => void,
): NotificationToastData => {
  return createSuccessToast(
    'Attached successfully',
    `"${fileName}" has been attached to ${patientName}'s claim.`,
    'View claim',
    onViewClaim,
  )
}

/**
 * Utility function to create a failed notification toast
 */
export const createFailedToast = (
  title: string,
  message: string,
  actionButtonText?: string,
): NotificationToastData => {
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.CRITICAL,
    title,
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message,
    actionButtonText: actionButtonText || '',
  }
}

/**
 * Toast after failing to save (create) a chart.
 */
export const createFailedToSaveChartToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Failed to save chart',
    errorMessage?.trim() || 'Failed to save chart. Please try again.',
  )
}

/**
 * Toast after failing to update a chart.
 */
export const createFailedToUpdateChartToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Update failed',
    errorMessage?.trim() || 'Failed to save chart. Please try again.',
  )
}

/**
 * Toast after failing to delete a chart.
 */
export const createFailedToDeleteChartToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Failed to delete chart',
    errorMessage?.trim() || 'Failed to delete chart. Please try again.',
  )
}

/**
 * Toast after failing to create a treatment plan.
 */
export const createFailedToCreateTreatmentPlanToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Failed to create treatment plan',
    errorMessage?.trim() ||
      'Failed to create treatment plan. Please try again.',
  )
}

/**
 * Toast after failing to update a treatment plan.
 */
export const createFailedToUpdateTreatmentPlanToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Failed to update treatment plan',
    errorMessage?.trim() ||
      'Failed to update treatment plan. Please try again.',
  )
}

/**
 * Toast after failing to delete a treatment plan.
 */
export const createFailedToDeleteTreatmentPlanToast = (
  errorMessage?: string,
): NotificationToastData => {
  return createFailedToast(
    'Failed to delete treatment plan',
    errorMessage?.trim() ||
      'Failed to delete treatment plan. Please try again.',
  )
}

export const createNewChatToast = (
  title: string,
  messagePreview?: string,
  icon = 'mgc_mail_fill',
): NotificationToastData => {
  const preview = (messagePreview || '').trim()
  const message = preview
    ? `${preview.slice(0, 80)}${preview.length > 80 ? '...' : ''}`
    : 'New message in a conversation'
  return {
    id: `toast-chat-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.SUCCESS,
    title,
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message,
    actionButtonText: 'Open chat',
    icon,
  }
}

/**
 * Utility function to create a notification toast when a patient has signed/completed forms.
 * Used by Form Builder completion polling to notify staff.
 * Pass onAction to run when the user clicks "View forms" (e.g. navigate to Form Builder).
 */
/**
 * Utility function to map a backend notification (already mapped to UI shape) to a
 * notification toast. Priority is forwarded directly so colour/icon stay consistent
 * with the Notifications page.
 */
export const createNotificationToast = (
  notification: {
    id?: string
    notification_id?: string
    title: string
    message: string
    actionButtonText?: string | null
    priority: NotificationPriority
  },
  onAction?: () => void,
): NotificationToastData => {
  return {
    id: `toast-notif-${notification.notification_id ?? notification.id ?? Date.now()}-${Math.random()}`,
    priority: notification.priority,
    title: notification.title,
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message: notification.message,
    actionButtonText: notification.actionButtonText ?? '',
    ...(onAction && { onAction }),
  }
}

export const createPatientSignedFormsToast = (
  patientName: string,
  formCount?: number,
  onAction?: () => void,
): NotificationToastData => {
  const message =
    formCount !== undefined && formCount > 1
      ? `${patientName} has signed ${formCount} forms.`
      : `${patientName} has signed 1 form.`
  return {
    id: `toast-${Date.now()}-${Math.random()}`,
    priority: NotificationPriority.SUCCESS,
    title: 'Forms signed',
    date: moment().format('MMMM D, YYYY'),
    time: moment().format('h:mm A'),
    message,
    actionButtonText: 'View forms',
    ...(onAction && { onAction }),
  }
}

export const createNewMemberToast = (
  patientName: string,
  planName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  const plan = planName.trim() || 'membership'
  return createSuccessToast(
    'New member',
    `${name} has been enrolled to a ${plan} membership plan.`,
  )
}

export const createMemberPlanUpdatedToast = (
  patientName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  return createSuccessToast(
    'Member plan updated successfully',
    `${name}\u2019s membership plan has been updated.`,
  )
}

export const createMembershipCanceledToast = (
  patientName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  return createSuccessToast(
    'Membership canceled',
    `${name}\u2019s membership plan has been canceled.`,
  )
}

export const createMembershipOnHoldToast = (
  patientName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  return createSuccessToast(
    'Membership on-hold',
    `${name}\u2019s membership plan has been temporarily put on-hold.`,
  )
}

export const createMembershipReactivatedToast = (
  patientName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  return createSuccessToast(
    'Reactivated membership successfully',
    `${name}\u2019s membership plan has been reactivated.`,
  )
}

export const createPaymentDetailsUpdatedToast = (
  patientName: string,
): NotificationToastData => {
  const name = patientName.trim() || 'Patient'
  return createSuccessToast(
    'Successfully updated payment details',
    `${name}\u2019s payment details has been updated.`,
  )
}

export const createEmailSentToast = (): NotificationToastData =>
  createSuccessToast(
    'Sent successfully',
    'The email has been sent to the recipients.',
  )

export const createEmailForwardedToast = (): NotificationToastData =>
  createSuccessToast(
    'Forwarded successfully',
    'The email has been forwarded to the recipients.',
  )

export const createEmailLabelsAddedToast = (): NotificationToastData =>
  createSuccessToast(
    'Labels added successfully',
    'Labels were added to selected emails.',
  )

export const createEmailMovedToTrashToast = (): NotificationToastData =>
  createSuccessToast(
    'Move to trash',
    'Selected emails were moved to trash. You may go to Trash to restore them.',
  )

export const createEmailFolderCreatedToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast('New folder', `"${folderName}" has been created.`)

export const createEmailMovedToFolderToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Moved successfully',
    `Selected emails have been moved to the "${folderName}" folder.`,
  )

export const createEmailRestoredToast = (): NotificationToastData =>
  createSuccessToast(
    'Email restored',
    'Selected emails were restored to the Inbox.',
  )

export const createEmailFolderRenamedToast = (
  newFolderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Renamed successfully',
    `Email folder has been renamed to "${newFolderName}".`,
  )

export const createEmailLabelCreatedToast = (
  labelName: string,
): NotificationToastData =>
  createSuccessToast(
    'Created successfully',
    `"${labelName}" email label has been created.`,
  )

export const createEmailLabelRenamedToast = (
  labelName: string,
): NotificationToastData =>
  createSuccessToast(
    'Renamed successfully',
    `Email label "${labelName}" has been updated.`,
  )

export const createEmailLabelDeletedToast = (
  labelName: string,
): NotificationToastData =>
  createSuccessToast(
    'Deleted successfully',
    `"${labelName}" email label has been deleted.`,
  )

export const createEmailFolderDeletedToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Deleted successfully',
    `"${folderName}" email folder has been deleted. All emails inside are now back in your inbox.`,
  )

export const createFaxPageLimitReachedToast = (
  totalPages: number,
  maxPages: number,
): NotificationToastData =>
  createFailedToast(
    'Page limit reached',
    `This fax has ${totalPages} page(s), which exceeds the maximum of ${maxPages}. Remove documents or shorten the cover sheet to continue.`,
  )

export const createFaxCoversheetTemplateSavedToast =
  (): NotificationToastData =>
    createSuccessToast('Saved successfully', 'Cover sheet template saved.')

export const createFaxCoversheetTemplateSaveFailedToast =
  (): NotificationToastData =>
    createFailedToast('Save failed', 'Could not save template.')

export const createFaxNoRecipientToast = (): NotificationToastData =>
  createFailedToast(
    'No recipient',
    'A recipient is required before sending fax.',
  )

export const createFaxSentToast = (): NotificationToastData =>
  createSuccessToast(
    'Sent successfully',
    'Your fax has been queued for sending.',
  )

export const createFaxResendingToast = (): NotificationToastData =>
  createSuccessToast('Resending fax', 'The fax is being resent.')

export const createFaxResendFailedToast = (): NotificationToastData =>
  createFailedToast('Retry failed', 'Could not resend the fax.')

export const createFaxMovedToTrashToast = (): NotificationToastData =>
  createSuccessToast(
    'Moved to trash successfully',
    'Selected faxes were moved to trash.',
  )

export const createFaxRestoredToInboxToast = (): NotificationToastData =>
  createSuccessToast(
    'Restored successfully',
    'Selected faxes were restored to the Inbox.',
  )

export const createFaxMovedToFolderToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Moved successfully',
    `Selected faxes have been moved to "${folderName}" folder.`,
  )

export const createFaxFolderCreatedToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast('New folder', `"${folderName}" has been created.`)

export const createFaxFolderRenamedToast = (
  newFolderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Renamed successfully',
    `Fax folder has been renamed to "${newFolderName}".`,
  )

export const createFaxFolderDeletedToast = (
  folderName: string,
): NotificationToastData =>
  createSuccessToast(
    'Deleted successfully',
    `"${folderName}" fax folder has been deleted. All faxes inside are now back in your inbox.`,
  )
